import React, { useState } from "react";
import API from "../services/api";
import { sendPortalWhatsApp } from "../utils/whatsapp";
import "./EmployeeSalaryPage.css";

const fmtMins = (mins) => {
  if (!mins && mins !== 0) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};

const fmtMoney = (n) => `₹${Number(n || 0).toFixed(2)}`;

const EmployeeSalaryPage = () => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await API.get("/attendance/work-summary", { params: { from, to } });
      setData(res.data);
      if (!res.data?.records?.length) setMessage("No records found.");
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load salary summary.");
    } finally {
      setLoading(false);
    }
  };

  const salary = Number(data?.workProfile?.salaryMonthly || 0);
  const overtimePay = Number(data?.totals?.overtimePay || 0);
  const latePenalty = Number(data?.totals?.latePenalty || 0);
  const finalPay = salary + overtimePay - latePenalty;
  const lateDays =
    (data?.records || []).filter((r) => Number(r.lateMinutes || 0) > 0).length || 0;
  const overtimeDays =
    (data?.records || []).filter((r) => Number(r.overtimeMinutes || 0) > 0).length || 0;

  const sendSalaryWhatsApp = async () => {
    if (!data) return;
    try {
      const meRes = await API.get("/auth/me");
      const me = meRes.data || {};
      const phone = String(me.mobile || "").trim();
      const employeeName = String(me.fullName || data?.employee || "Employee").trim();
      if (!phone) {
        setMessage("Your mobile number is not available for WhatsApp.");
        return;
      }

      const ok = window.confirm(
        `Send salary summary on WhatsApp to ${employeeName} (${phone})?`
      );
      if (!ok) return;

      setSendingWhatsApp(true);
      await sendPortalWhatsApp({
        to: phone,
        recipientName: employeeName,
        message:
          `Hello ${employeeName}, salary summary for selected range:\n` +
          `Base Salary: ${fmtMoney(salary)}\n` +
          `Overtime Pay: ${fmtMoney(overtimePay)}\n` +
          `Late Penalty: ${fmtMoney(latePenalty)}\n` +
          `Final Net Salary: ${fmtMoney(finalPay)}`,
        sourceModule: "employee_salary_page",
        eventType: "manual_salary_summary",
        entityType: "AttendanceSummary",
        entityId: `${from || "na"}_${to || "na"}`,
        isAdmin: false,
      });
      setMessage("Salary summary sent on WhatsApp.");
    } catch (err) {
      setMessage(err?.response?.data?.message || "Failed to send salary summary on WhatsApp.");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  return (
    <div className="salary-page">
      <header className="salary-header">
        <div>
          <div className="salary-title">Salary Summary</div>
          <div className="salary-subtitle">
            Your monthly work, overtime, and late deductions in one view.
          </div>
        </div>
        {data ? (
          <button
            className="salary-btn"
            type="button"
            onClick={sendSalaryWhatsApp}
            disabled={sendingWhatsApp}
          >
            {sendingWhatsApp ? "Sending..." : "Send on WhatsApp"}
          </button>
        ) : null}
      </header>

      <section className="salary-filter">
        <div className="salary-field">
          <label>From Date</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="salary-field">
          <label>To Date</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="salary-btn" onClick={fetchSummary} disabled={loading}>
          {loading ? "Loading..." : "Fetch Summary"}
        </button>
      </section>

      {message && <div className="salary-note">{message}</div>}

      {data && (
        <>
          <section className="salary-kpis">
            <div className="kpi-card">
              <div className="kpi-label">Monthly Base Salary</div>
              <div className="kpi-value">{fmtMoney(salary)}</div>
              <div className="kpi-foot">Fixed monthly amount</div>
            </div>
            <div className="kpi-card">
              <div className="kpi-label">Total Working Days</div>
              <div className="kpi-value">{data.totals?.totalDays || 0}</div>
              <div className="kpi-foot">Days present in range</div>
            </div>
            <div className="kpi-card danger">
              <div className="kpi-label">Late Days</div>
              <div className="kpi-value">{lateDays}</div>
              <div className="kpi-foot">Late time: {fmtMins(data.totals?.lateMinutes)}</div>
            </div>
            <div className="kpi-card accent">
              <div className="kpi-label">Overtime Days</div>
              <div className="kpi-value">{overtimeDays}</div>
              <div className="kpi-foot">OT: {fmtMins(data.totals?.overtimeMinutes)}</div>
            </div>
            <div className="kpi-card accent">
              <div className="kpi-label">Overtime Pay</div>
              <div className="kpi-value">{fmtMoney(overtimePay)}</div>
              <div className="kpi-foot">Extra earnings</div>
            </div>
            <div className="kpi-card danger">
              <div className="kpi-label">Late Penalty</div>
              <div className="kpi-value">{fmtMoney(latePenalty)}</div>
              <div className="kpi-foot">Total deductions</div>
            </div>
            <div className="kpi-card net">
              <div>
                <div className="kpi-label">Final Net Salary</div>
                <div className="kpi-value">{fmtMoney(finalPay)}</div>
                <div className="kpi-foot">Salary + OT − Penalty</div>
              </div>
              <div className="kpi-net-icon">₹</div>
            </div>
          </section>

          <section className="salary-table">
            <div className="salary-table-header">
              <div className="salary-table-title">Daily Attendance & Earnings</div>
              <div className="salary-table-sub">Net adjustment = OT pay − late penalty</div>
            </div>
            <div className="salary-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Punch In</th>
                    <th>Punch Out</th>
                    <th>Work</th>
                    <th>Late</th>
                    <th>Overtime</th>
                    <th>OT Pay</th>
                    <th>Late Penalty</th>
                    <th className="right">Net Adjust</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.records || []).map((r) => {
                    const rowOvertimePay = Number(r.overtimePay || 0);
                    const rowLatePenalty = Number(r.latePenalty || 0);
                    const netAdj = rowOvertimePay - rowLatePenalty;
                    const netClass = netAdj > 0 ? "pos" : netAdj < 0 ? "neg" : "";
                    return (
                      <tr key={r._id}>
                        <td>{r.date ? new Date(r.date).toLocaleDateString() : "-"}</td>
                        <td>{r.punchIn?.time ? new Date(r.punchIn.time).toLocaleString() : "-"}</td>
                        <td>{r.punchOut?.time ? new Date(r.punchOut.time).toLocaleString() : "-"}</td>
                        <td>{fmtMins(r.workMinutes)}</td>
                        <td>{fmtMins(r.lateMinutes)}</td>
                        <td>{fmtMins(r.overtimeMinutes)}</td>
                        <td>{fmtMoney(rowOvertimePay)}</td>
                        <td>{fmtMoney(rowLatePenalty)}</td>
                        <td className={`right ${netClass}`}>{fmtMoney(netAdj)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default EmployeeSalaryPage;
